import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { activityFlatOfInfo } from '@components/pages/activity-log/services/activity-log-flat.util';
import { ApiService } from '@services/api/api.service';
import { BookingClientService } from '@services/api/booking/booking-client.service';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { HostActivityLogService } from '../../../shared/host-activity-log.service';
import { bookingRefMetadata } from '../../../shared/host-activity.metadata';
import { ArrivalBookingRow } from '../../models/booking-overview.model';

type AlternateContactType = 'booking' | 'user' | 'checkin';

interface AlternateContact {
  value: string;
  type: AlternateContactType;
}

const ALTERNATE_CONTACT_META: Record<AlternateContactType, { icon: string; labelKey: string }> = {
  booking: { icon: 'bookmark', labelKey: 'bookingOverview.checkinSms.alternateBookingPhone' },
  user: { icon: 'user', labelKey: 'bookingOverview.checkinSms.alternateUserPhone' },
  checkin: { icon: 'id-card', labelKey: 'bookingOverview.checkinSms.alternateCheckinPhone' }
};

@Component({
  selector: 'app-checkin-sms-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, DialogModule, ButtonModule, InputTextModule, TooltipModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <p-dialog
      [visible]="visible()"
      [modal]="true"
      [closable]="!sending()"
      [draggable]="false"
      [resizable]="false"
      [closeOnEscape]="!sending()"
      styleClass="w-full max-w-md"
      (onHide)="onCancel()"
      (visibleChange)="onVisibilityChange($event)"
    >
      <ng-template pTemplate="header">
        <div class="flex items-center gap-3">
          <i class="pi pi-mobile text-xl text-primary"></i>
          <span class="text-lg font-semibold">{{ 'bookingOverview.checkinSms.title' | translate }}</span>
        </div>
      </ng-template>

      @if (booking(); as row) {
        <div class="space-y-5">
          <div class="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
            <div class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <i class="pi pi-user text-gray-500"></i>
              <span class="font-medium">{{ row.guest.name || '-' }}</span>
            </div>
            @if (row.flatInfos.length) {
              <div class="mt-2 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <i class="pi pi-home text-gray-500"></i>
                <span>{{ row.flatInfos[0].buildingName }} · {{ row.flatInfos[0].flatName }}</span>
              </div>
            }
            <div class="mt-2 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <i class="pi pi-link text-gray-500"></i>
              <span class="break-all font-mono text-xs">{{ checkinLink() }}</span>
            </div>
          </div>

          <div>
            <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300" for="checkinSmsPhone">
              {{ 'bookingOverview.checkinSms.recipientLabel' | translate }}
            </label>
            <input
              id="checkinSmsPhone"
              pInputText
              type="tel"
              class="w-full"
              [ngModel]="phoneInput()"
              (ngModelChange)="phoneInput.set($event)"
              [disabled]="sending()"
              autocomplete="tel"
              inputmode="tel"
            />

            @if (alternatePhone(); as alt) {
              <div class="mt-3 flex flex-wrap items-center gap-2">
                <span class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  {{ 'bookingOverview.checkinSms.alternateLabel' | translate }}:
                </span>
                <button
                  type="button"
                  [disabled]="sending()"
                  class="inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-3 py-1 text-sm text-gray-700 transition-colors hover:border-primary hover:bg-primary-50 hover:text-primary disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                  (click)="onUseAlternate(alt.value)"
                >
                  <i class="pi pi-{{ alternateMeta[alt.type].icon }} text-xs"></i>
                  <span>{{ alternateMeta[alt.type].labelKey | translate: { phone: alt.value } }}</span>
                </button>
              </div>
            }
          </div>
        </div>
      }

      <ng-template pTemplate="footer">
        <div class="flex justify-end gap-2">
          <button
            pButton
            type="button"
            class="p-button-text"
            [label]="'bookingOverview.checkinSms.cancel' | translate"
            [disabled]="sending()"
            (click)="onCancel()"
          ></button>
          <button
            pButton
            type="button"
            icon="pi pi-send"
            [label]="(sending() ? 'bookingOverview.checkinSms.sending' : 'bookingOverview.checkinSms.send') | translate"
            [loading]="sending()"
            [disabled]="!canSend()"
            (click)="onSend()"
          ></button>
        </div>
      </ng-template>
    </p-dialog>
  `
})
export class CheckinSmsModalComponent {
  visible = input<boolean>(false);
  booking = input<ArrivalBookingRow | null>(null);

  closed = output<void>();
  sent = output<void>();

  protected phoneInput = signal<string>('');
  protected sending = signal<boolean>(false);

  private readonly apiService = inject(ApiService);
  private readonly bookingClientService = inject(BookingClientService);
  private readonly messageService = inject(MessageService);
  private readonly translateService = inject(TranslateService);
  private readonly hostActivityLog = inject(HostActivityLogService);

  protected readonly checkinLink = computed(() => {
    const code = this.booking()?.reservationCode;
    if (!code) return '';
    const baseUrl = this.apiService.getBaseUrl();
    const domain = baseUrl.includes('teszt') ? 'teszt.i11.hu' : 'i11.hu';
    return `https://${domain}/cin/${code}`;
  });

  protected readonly alternateMeta = ALTERNATE_CONTACT_META;

  protected readonly alternatePhone = computed<AlternateContact | null>(() => {
    const guest = this.booking()?.guest;
    if (!guest) return null;
    const bookingPhone = guest.bookingPhone?.trim();
    const userPhone = guest.phone?.trim();
    if (!bookingPhone || !userPhone || bookingPhone === userPhone) return null;
    const current = this.phoneInput().trim();
    const userType: AlternateContactType = guest.phoneSource === 'checkin' ? 'checkin' : 'user';
    if (current === bookingPhone) return { value: userPhone, type: userType };
    if (current === userPhone) return { value: bookingPhone, type: 'booking' };
    return { value: bookingPhone, type: 'booking' };
  });

  protected readonly canSend = computed(() => !this.sending() && this.phoneInput().trim().length >= 6);

  constructor() {
    effect(() => {
      if (this.visible()) {
        const guest = this.booking()?.guest;
        const prefill = guest?.bookingPhone?.trim() || guest?.phone?.trim() || '';
        this.phoneInput.set(prefill);
        this.sending.set(false);
      }
    });
  }

  protected onUseAlternate(value: string): void {
    this.phoneInput.set(value);
  }

  protected onCancel(): void {
    if (this.sending()) return;
    this.closed.emit();
  }

  protected onVisibilityChange(open: boolean): void {
    if (!open && !this.sending()) {
      this.closed.emit();
    }
  }

  protected onSend(): void {
    const row = this.booking();
    const phone = this.phoneInput().trim();
    if (!row || !this.canSend()) return;

    this.sending.set(true);
    this.bookingClientService.sendCheckinSms(row.id, phone).subscribe({
      next: () => {
        this.hostActivityLog.log('checkin_sms_send', bookingRefMetadata({ bookingId: row.id, flats: row.flatInfos.map(activityFlatOfInfo) }));
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('common.success'),
          detail: this.translateService.instant('bookingOverview.checkinSms.success'),
          life: 3000
        });
        this.sending.set(false);
        this.sent.emit();
        this.closed.emit();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.error'),
          detail: this.translateService.instant('bookingOverview.checkinSms.error'),
          life: 5000
        });
        this.sending.set(false);
      }
    });
  }
}
